<template>
  <div class="row-root" :class="{ inside: isInside }">
    <div class="row-main">
      <div class="icon-well" aria-hidden="true">{{ icon }}</div>
      <div class="content">
        <div class="topline">
          <div class="name">{{ location.name }}</div>
          <UiChip v-if="isInside" variant="active">Visiting</UiChip>
          <UiChip v-else-if="isBlocked" variant="blocked" :title="blockedReason">Blocked</UiChip>
        </div>
        <div class="subline text-muted">
          <span>{{ location.type }}</span>
          <template v-if="serviceChips.length">
            <span class="dot">•</span>
            <span class="services">
              <template v-for="chip in serviceChips" :key="chip.label">
                <button
                  v-if="isInside && chip.service"
                  type="button"
                  class="chip-action"
                  :disabled="disabled || isBlocked"
                  :title="isBlocked ? blockedReason ?? undefined : `Open ${chip.label}`"
                  @click="emit('open-service', chip.service)"
                >
                  <UiChip variant="muted">{{ chip.icon }} {{ chip.label }}</UiChip>
                </button>
                <UiChip v-else variant="muted">{{ chip.icon }} {{ chip.label }}</UiChip>
              </template>
            </span>
          </template>
        </div>
      </div>
    </div>

    <div class="row-actions">
      <UiButton
        v-if="isInside"
        size="sm"
        variant="secondary"
        :disabled="disabled"
        @click="emit('exit', location.id)"
      >
        Leave
      </UiButton>
      <UiButton
        v-else
        size="sm"
        variant="secondary"
        :disabled="disabled || isBlocked"
        :title="isBlocked ? blockedReason : 'Visit'"
        @click="emit('enter', location.id)"
      >
        Visit
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Location } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import UiButton from "../../ui/UiButton.vue";
import UiChip from "../../ui/UiChip.vue";

const props = defineProps<{
  location: Location;
  isInside: boolean;
  disabled?: boolean;
  isBlocked?: boolean;
  blockedReason?: string | null;
}>();

const emit = defineEmits<{
  enter: [locationId: string];
  exit: [locationId: string];
  "open-service": [service: "bank" | "exchange"];
}>();

const icon = computed(() => {
  const services = props.location.services || [];
  if (services.includes("bank")) return ICONS.places.bank;
  if (services.includes("exchange")) return ICONS.places.exchange;
  return ICONS.places.building;
});

const STATION_LABEL_BY_LOCATION_TYPE: Record<string, string> = {
  smithy: "Smithy",
  hut: "Hearth",
  workshop: "Workshop Bench",
  tower: "Arcane Altar"
};

const stationLabel = computed(() => {
  const type = (props.location.type || "").toString();
  return STATION_LABEL_BY_LOCATION_TYPE[type] || null;
});

const serviceChips = computed(() => {
  const services = props.location.services || [];
  return [
    // Both services are gated on the "bank" key, matching the world header and the server
    // (economy.ts and banking.ts each check service_key = 'bank'). There is no separate
    // "exchange"/"market" key -- the Exchange terminal lives at banks by design.
    services.includes("bank") ? { icon: ICONS.places.bank, label: "Bank", service: "bank" as const } : null,
    services.includes("bank")
      ? { icon: ICONS.places.exchange, label: "Exchange", service: "exchange" as const }
      : null,
    stationLabel.value ? { icon: ICONS.places.station, label: stationLabel.value, service: null } : null
  ].filter(Boolean) as Array<{ icon: string; label: string; service: "bank" | "exchange" | null }>;
});
</script>

<style scoped>
.row-root {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.75rem 0.8rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
}

.row-root.inside {
  border-color: color-mix(in srgb, var(--accent) 35%, var(--glass-border-0));
  background: color-mix(in srgb, var(--accent) 8%, var(--glass-0));
}

.row-main {
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  min-width: 0;
}

.icon-well {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
  flex: 0 0 auto;
}

.content {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
}

.topline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.name {
  font-weight: 800;
}

.subline {
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.dot {
  opacity: 0.8;
}

.services {
  display: inline-flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.chip-action {
  all: unset;
  cursor: pointer;
  border-radius: var(--radius-pill);
}

.chip-action:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.chip-action:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.chip-action:hover:not(:disabled) :deep(.ui-chip) {
  filter: brightness(1.15);
}

.row-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  justify-content: flex-end;
  justify-self: end;
  align-self: end;
}

@media (max-width: 720px) {
  .row-root {
    grid-template-columns: 1fr;
  }

  .row-actions {
    justify-content: flex-end;
  }
}
</style>
